import React, { useState } from 'react';
import ObjectTopBar from '../components/topbar/ObjectTopBar';
import useDocumentTitle from '../../shared/hooks/useDocumentTitle.js';
import useKitBatches from '../hooks/useKitBatches.js';
import useAdminKits from '../hooks/useAdminKits.js';
import KitBatchCard from '../components/kits/KitBatchCard.jsx';
import GenerateKitCodesModal from '../components/kits/GenerateKitCodesModal.jsx';
import LoadingView from '../../shared/components/LoadingView';

export default function KitBatchesPage() {
  useDocumentTitle('Kit codes – Maintella');

  const { batches, loading, error, generateBatch, exportBatchCsv } = useKitBatches();
  const { data: kits, loading: loadingKits } = useAdminKits();
  const [showGenerateModal, setShowGenerateModal] = useState(false);

  const handleGenerate = async (count, kitId, name) => {
    await generateBatch(count, kitId, name);
  };

  const handleExport = async (batchNumber) => {
    try {
      await exportBatchCsv(batchNumber);
    } catch (err) {
      alert(err.message || 'Failed to export CSV');
    }
  };

  return (
    <>
      <ObjectTopBar
        title="Kit codes"
        primaryBtn={{
          btnText: 'Generate kit codes',
          btnIcon: 'bi bi-plus-lg',
          onClick: () => setShowGenerateModal(true),
        }}
      />

      <div className="container py-4">
        <div className="mb-4">
          <h6 className="text-muted fw-normal mb-1">Kit code batches</h6>
          <p className="text-muted small mb-0">
            Generate and track single-use kit codes tied to specific spare and upgrade kits.
            These codes can be scanned in workflows to prove genuine parts usage.
          </p>
        </div>

        {loading && batches.length === 0 ? (
          <LoadingView
            symbolClass="bi-upc-scan"
            title="Loading kit code batches"
            subtitle="Fetching generated kit code batches."
            className="py-5"
          />
        ) : null}

        {error && !loading && (
          <div className="alert alert-danger">
            <i className="bi bi-exclamation-triangle me-2" />
            Failed to load kit code batches. Please try refreshing the page.
          </div>
        )}

        {!loading && !error && batches.length === 0 && (
          <div className="card">
            <div className="card-body text-center py-5">
              <i className="bi bi-upc-scan display-1 text-muted opacity-50 mb-3 d-block" />
              <h5 className="text-muted">No kit code batches yet</h5>
              <p className="text-muted mb-4">
                Generate your first batch of kit codes to ship with spare and upgrade kits.
                Each code can be used once inside a workflow to verify genuine parts.
              </p>
              <button
                className="btn btn-primary"
                type="button"
                onClick={() => setShowGenerateModal(true)}
              >
                <i className="bi bi-plus-lg me-2" />
                Generate kit codes
              </button>
            </div>
          </div>
        )}

        {!loading && !error && batches.length > 0 && (
          <div>
            {batches.map((batch) => (
              <KitBatchCard
                key={batch.id}
                batch={batch}
                onExport={handleExport}
              />
            ))}
          </div>
        )}
      </div>

      <GenerateKitCodesModal
        show={showGenerateModal}
        onClose={() => setShowGenerateModal(false)}
        onGenerate={handleGenerate}
        kits={kits}
        loadingKits={loadingKits}
      />
    </>
  );
}


